import { makeStyles, tokens } from '@fluentui/react-components'
import { VacationBalance } from '../services/vacationBalanceService'

const useStyles = makeStyles({
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(108px, 1fr))',
    gap: '8px',
  },
  tile: {
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    padding: '10px 12px',
  },
  tileLabel: { fontSize: '12px', marginBottom: '4px' },
  tileHours: { fontSize: '22px', fontWeight: 700, lineHeight: 1.1 },
  tileDays:  { fontSize: '13px' },
  progressWrap: { marginTop: '14px' },
  track: {
    display: 'flex',
    height: '8px',
    borderRadius: '999px',
    overflow: 'hidden',
    backgroundColor: tokens.colorNeutralBackground4,
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '14px',
    marginTop: '8px',
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  legendItem: { display: 'inline-flex', alignItems: 'center', gap: '5px' },
  swatch: { width: '9px', height: '9px', borderRadius: '2px' },
})

function fmt(v: number) {
  return v % 1 === 0 ? String(v) : v.toFixed(1).replace('.', ',')
}

export default function VacationTiles({ balance }: { balance: VacationBalance }) {
  const styles = useStyles()

  const avg = balance.averageDailyHours
  const availHours = Math.round((balance.totalHours - balance.usedHours - balance.plannedHours) * 10) / 10
  const availDays  = avg > 0 ? Math.round((availHours / avg) * 10) / 10 : 0

  const usedPct = balance.totalHours > 0 ? balance.usedHours / balance.totalHours : 0
  const planPct = balance.totalHours > 0 ? balance.plannedHours / balance.totalHours : 0
  const availPct = Math.max(0, 1 - usedPct - planPct)

  const availColor = availPct > 0.3 ? tokens.colorPaletteGreenForeground1
                   : availPct > 0.1 ? tokens.colorPaletteYellowForeground1
                   : tokens.colorPaletteRedForeground1
  const planColor  = tokens.colorStatusWarningForeground1
  const usedColor  = tokens.colorBrandForeground1
  const neutral    = tokens.colorNeutralForeground1
  const muted      = tokens.colorNeutralForeground3

  const year     = balance.year
  const prevYear = balance.year - 1
  const workloadChanged = balance.workloadChangedInYear   // #24: dny jsou zavádějící → jen hodiny

  const tiles = [
    { kind: 'remaining',   label: 'Zbývá čerpání',           hours: availHours,               days: availDays,           color: availColor, bg: tokens.colorNeutralBackground3 },
    { kind: 'used',        label: 'Vyčerpáno k dnešku',      hours: balance.usedHours,        days: balance.used,        color: usedColor,  bg: tokens.colorNeutralBackground2 },
    { kind: 'planned',     label: 'Naplánováno',             hours: balance.plannedHours,     days: balance.plannedDays, color: planColor,  bg: tokens.colorNeutralBackground2 },
    { kind: 'entitlement', label: `Nárok pro ${year}`,       hours: balance.entitlementHours, days: balance.entitlement, color: neutral,    bg: tokens.colorNeutralBackground2 },
    { kind: 'carried',     label: `Převedeno z ${prevYear}`, hours: balance.carriedOverHours, days: balance.carriedOver, color: neutral,    bg: tokens.colorNeutralBackground2 },
    { kind: 'total',       label: `Celkem pro ${year}`,      hours: balance.totalHours,       days: balance.total,       color: neutral,    bg: tokens.colorNeutralBackground2 },
  ]

  return (
    <div>
      <div className={styles.grid}>
        {tiles.map(t => (
          <div key={t.label} className={styles.tile} style={{ background: t.bg }}>
            <div className={styles.tileLabel} style={{ color: t.color }}>{t.label}</div>
            <div className={styles.tileHours} style={{ color: t.color }}>{fmt(t.hours)} h</div>
            {(() => {
              // Dny: pro Vyčerpáno/Naplánováno jsou přesné (součet skutečných dnů žádostí) a pro
              // „Zbývá čerpání" z aktuálního úvazku → ukážeme vždy. Nárok/Převedeno/Celkem jsou souhrny
              // v hodinách: při změně úvazku během roku by dny mátly (míchají úvazky) → jen hodiny (u Nároku nominální).
              const daysAlways = t.kind === 'remaining' || t.kind === 'used' || t.kind === 'planned'
              const dayLine = daysAlways
                ? `${fmt(t.days)} dní`
                : workloadChanged
                  ? (t.kind === 'entitlement' ? '25 dnů na rok' : null)
                  : `${fmt(t.days)} dní`
              return dayLine === null ? null : (
                <div className={styles.tileDays} style={{ color: t.color === neutral ? muted : t.color }}>
                  {dayLine}
                </div>
              )
            })()}
          </div>
        ))}
      </div>

      <div className={styles.progressWrap}>
        <div className={styles.track}>
          <div style={{ width: `${Math.min(100, usedPct * 100)}%`, background: usedColor }} />
          <div style={{ width: `${Math.min(100, planPct * 100)}%`, background: planColor }} />
        </div>
        <div className={styles.legend}>
          <span className={styles.legendItem}>
            <span className={styles.swatch} style={{ background: usedColor }} />
            Vyčerpáno {fmt(balance.usedHours)} h
          </span>
          <span className={styles.legendItem}>
            <span className={styles.swatch} style={{ background: planColor }} />
            Naplánováno {fmt(balance.plannedHours)} h
          </span>
          <span className={styles.legendItem}>
            <span className={styles.swatch} style={{ background: availColor }} />
            Zbývá {fmt(availHours)} h
          </span>
          <span style={{ marginLeft: 'auto', color: tokens.colorNeutralForeground4 }}>
            z {fmt(balance.totalHours)} h celkem
          </span>
        </div>
      </div>
    </div>
  )
}
